import { ObjectBadgeMapper } from '@frontend/components/common';
import { DateBadge } from '@frontend/elements';
import { CommonMessage } from '@frontend/lang';
import { Report } from '@frontend/report/types';
import { TransactionState, TransactionStateName } from '@frontend/transaction/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ActionModal } from '../action-modal/action-modal.component';
import { useExportModal } from './export-modal.controller';

export interface ExportModalProps {
    modalId: string;
    report: Report;
    onCancel?: () => void;
    data?: { [key: string]: any };
    searchKeys?: string[];
    dataKeys?: string[];
    children?: React.ReactNode;
    exportDisabled?: boolean;
}

export const ExportModal = (props: ExportModalProps) => {
    const viewProps = useExportModal(props);
    return (
        <ActionModal
            title={props.report.name}
            cancel={{
                onCancel: () => (props.onCancel ? props.onCancel() : viewProps.closeModal(props.modalId)),
                label: (
                    <FormattedMessage
                        id='export-modal.cancel-export'
                        defaultMessage='Cancel export'
                    />
                ),
                hide: true
            }}
            confirm={{
                onConfirm: () => viewProps.handleExport(),
                label: !viewProps.user?.email ? (
                    <FormattedMessage
                        id='export-modal.no-email'
                        defaultMessage='No email associated with your user'
                    />
                ) : (
                    <FormattedMessage
                        id='export-modal.confirm-export'
                        defaultMessage='Email to: {email}'
                        values={{ email: viewProps.user?.email }}
                    />
                ),
                disabled: !viewProps.user?.email || props.exportDisabled
            }}
            additionalButtons={[
                {
                    onClick: () => viewProps.handleDownloadExport(),
                    label: CommonMessage.BUTTONS.DOWNLOAD,
                    disabled: props.exportDisabled
                }
            ]}>
            <div className='inline-flex w-full flex-col items-center justify-start'>
                <div className='w-full'>
                    {props.children}
                    {viewProps.extraData && Object.keys(viewProps.extraData).length > 0 && (
                        <div className='mt-4'>
                            <h3 className='text-foreground-default text-sm font-medium'>
                                <FormattedMessage
                                    id='export-modal.applied-filters'
                                    defaultMessage='Applied filters'
                                />
                            </h3>
                            <div className='mt-2 space-y-2 text-sm text-gray-500 dark:text-gray-400'>
                                {convertFiltersToBadges(viewProps.extraData).map((badge, index) => (
                                    <div
                                        key={index}
                                        className='border-border-default bg-background-default text-foreground-default flex items-center space-x-2 rounded-md border p-2 text-sm'>
                                        <span className='font-medium'>{badge.key}:</span>
                                        {badge.display}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ActionModal>
    );
};

const convertFiltersToBadges = (filters: { [key: string]: any }) => {
    const badges: { key: React.ReactNode; display: React.ReactNode }[] = [];

    Object.entries(filters).forEach(([key, value]) => {
        if (key === 'period_to_timestamp' || key === 'period_from_timestamp') {
            badges.push({
                key: formatFilterKey(key),
                display: <DateBadge date={new Date(value as string)} />
            });
            return;
        }
        if (key === 'data') {
            Object.entries(value).forEach(([dataKey, dataValue]) => {
                if (dataKey.endsWith('_id')) {
                    badges.push({
                        key: formatFilterKey(dataKey),
                        display: (
                            <ObjectBadgeMapper
                                id={dataValue as string}
                                type={dataKey}
                                disableClick
                            />
                        )
                    });
                } else {
                    if (Array.isArray(dataValue)) {
                        if (dataValue.every((item) => typeof item === 'string' && item.startsWith('transaction_'))) {
                            badges.push({
                                key: formatFilterKey(dataKey),
                                display: (
                                    <div className='flex flex-col'>
                                        {dataValue.map((item) => (
                                            <div key={item}>{TransactionState.getByValue(item as TransactionStateName)?.displayName}</div>
                                        ))}
                                    </div>
                                )
                            });
                        }
                    } else {
                        badges.push({
                            key: formatFilterKey(dataKey),
                            display: <span>{dataValue as string}</span>
                        });
                    }
                }
            });
        } else {
            badges.push({
                key: formatFilterKey(key),
                display: <span>{value as string}</span>
            });
        }
    });
    return badges;
};

const formatFilterKey = (key: string) => key.replace(/^period_/, '').replace(/_id$/, '').replace(/_/g, ' ').trim();
